"use client";

import { Book, FileText } from "lucide-react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { cn } from "@/lib/utils";

type DocItem = {
  title: string;
  slug: string;
  order?: number;
};

type DocSection = {
  title: string;
  items: DocItem[];
};

type DocsSidebarProps = {
  sections: DocSection[];
};

const linkClass = (active: boolean) =>
  cn(
    "block rounded-md px-2 py-1.5 text-sm transition-colors",
    active
      ? "bg-gold/10 font-medium text-foreground"
      : "text-muted-foreground hover:bg-secondary/60 hover:text-foreground",
  );

/**
 * Sticky documentation index. Sits below the site header instead of
 * behind it, and marks the current page.
 */
export function DocsSidebar({ sections }: DocsSidebarProps) {
  const pathname = usePathname();
  const isActive = (href: string) => pathname === href;

  return (
    <aside className="hidden w-60 shrink-0 lg:block">
      <div className="sticky top-[var(--header-height)] max-h-[calc(100svh-var(--header-height))] overflow-y-auto py-10 pr-6">
        <nav aria-label="Documentation" className="space-y-8">
          <div>
            <p className="mb-2 px-2 text-[10px] font-semibold uppercase tracking-[0.2em] text-muted-foreground/70">
              Quick links
            </p>
            <ul className="space-y-0.5">
              <li>
                <Link
                  href="/docs"
                  aria-current={isActive("/docs") ? "page" : undefined}
                  className={cn(
                    linkClass(isActive("/docs")),
                    "flex items-center gap-2",
                  )}
                >
                  <Book className="size-4" aria-hidden />
                  Documentation home
                </Link>
              </li>
              <li>
                <Link
                  href="/blog"
                  className={cn(linkClass(false), "flex items-center gap-2")}
                >
                  <FileText className="size-4" aria-hidden />
                  Blog
                </Link>
              </li>
            </ul>
          </div>

          {sections.map((section) => (
            <div key={section.title}>
              <p className="mb-2 px-2 text-[10px] font-semibold uppercase tracking-[0.2em] text-muted-foreground/70">
                {section.title}
              </p>
              <ul className="space-y-0.5">
                {section.items.map((item) => {
                  const href = `/docs/${item.slug}`;
                  const active = isActive(href);
                  return (
                    <li key={item.slug}>
                      <Link
                        href={href}
                        aria-current={active ? "page" : undefined}
                        className={linkClass(active)}
                      >
                        {item.title}
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </nav>
      </div>
    </aside>
  );
}
